import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { 
  Mail, Send, Clock, CheckCircle2, XCircle, Search, 
  RefreshCw, Copy, X 
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InvitationsManagement() {
  const [invitations, setInvitations] = useState([]);
  const [filteredInvitations, setFilteredInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchInvitations();
  }, []);

  useEffect(() => {
    filterInvitations();
  }, [invitations, searchTerm, statusFilter]);

  const fetchInvitations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/super-admin/invitations/list`);
      setInvitations(response.data);
    } catch (error) {
      console.error('Failed to fetch invitations:', error);
      toast.error('Failed to load invitations');
    } finally {
      setLoading(false);
    }
  };

  const filterInvitations = () => {
    let filtered = invitations;

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(inv => inv.status === statusFilter);
    }

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(inv =>
        inv.clinic_name.toLowerCase().includes(term) ||
        inv.admin_email.toLowerCase().includes(term) ||
        inv.subdomain.toLowerCase().includes(term)
      );
    }

    setFilteredInvitations(filtered);
  };

  const handleResend = async (invitationId) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/super-admin/invitations/resend/${invitationId}`
      );
      
      if (response.data.success) {
        toast.success('Invitation resent successfully!');
        fetchInvitations();
      }
    } catch (error) {
      console.error('Failed to resend invitation:', error);
      toast.error(error.response?.data?.detail || 'Failed to resend invitation');
    }
  };

  const handleCancel = async (invitationId) => {
    if (!window.confirm('Are you sure you want to cancel this invitation?')) {
      return;
    }

    try {
      const response = await axios.delete(
        `${API_URL}/api/super-admin/invitations/cancel/${invitationId}`
      );
      
      if (response.data.success) {
        toast.success('Invitation cancelled successfully');
        fetchInvitations();
      }
    } catch (error) {
      console.error('Failed to cancel invitation:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel invitation');
    }
  };

  const copyInvitationLink = (token) => {
    const link = `${window.location.origin}/accept-invitation/${token}`;
    navigator.clipboard.writeText(link);
    toast.success('Invitation link copied to clipboard!');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-blue-100 text-blue-800', icon: Clock, label: 'Pending' },
      accepted: { color: 'bg-green-100 text-green-800', icon: CheckCircle2, label: 'Accepted' },
      expired: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Expired' },
      cancelled: { color: 'bg-gray-100 text-gray-800', icon: X, label: 'Cancelled' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStats = () => {
    return {
      total: invitations.length,
      pending: invitations.filter(i => i.status === 'pending').length,
      accepted: invitations.filter(i => i.status === 'accepted').length,
      expired: invitations.filter(i => i.status === 'expired').length
    };
  };

  const stats = getStats();

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900">{stats.total}</div>
              <div className="text-sm text-gray-500">Total Invitations</div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-700">{stats.pending}</div>
              <div className="text-sm text-blue-600">Pending</div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-green-700">{stats.accepted}</div>
              <div className="text-sm text-green-600">Accepted</div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-red-700">{stats.expired}</div>
              <div className="text-sm text-red-600">Expired</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Invitations List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-purple-600" />
              Clinic Invitations
            </span>
            <Button size="sm" variant="outline" onClick={fetchInvitations}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </CardTitle>
          <CardDescription>
            Manage and track clinic invitations
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by clinic name, email, or subdomain..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div className="flex gap-2">
              {['all', 'pending', 'accepted', 'expired'].map((status) => (
                <Button
                  key={status}
                  size="sm"
                  variant={statusFilter === status ? 'default' : 'outline'}
                  onClick={() => setStatusFilter(status)}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </Button>
              ))}
            </div>
          </div>

          {/* Invitations Table */}
          {filteredInvitations.length === 0 ? (
            <div className="text-center py-12">
              <Mail className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No invitations found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredInvitations.map((invitation) => (
                <div
                  key={invitation.id}
                  className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-semibold text-lg">{invitation.clinic_name}</h4>
                        {getStatusBadge(invitation.status)}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4" />
                          {invitation.admin_email}
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          Invited: {formatDate(invitation.invited_at)}
                        </div>
                      </div>
                      
                      <div className="mt-2 text-sm text-gray-500">
                        Subdomain: <span className="font-mono bg-gray-100 px-2 py-1 rounded">{invitation.subdomain}</span>
                        {invitation.status === 'pending' && (
                          <span className="ml-4">
                            Expires: {formatDate(invitation.expires_at)}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      {invitation.status === 'pending' && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => copyInvitationLink(invitation.invitation_token)}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleResend(invitation.id)}
                          >
                            <Send className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => handleCancel(invitation.id)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      
                      {invitation.status === 'accepted' && invitation.accepted_at && (
                        <div className="text-sm text-green-600 flex items-center gap-1">
                          <CheckCircle2 className="h-4 w-4" />
                          Accepted {formatDate(invitation.accepted_at)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
